import React from 'react';
import { Alert, Autocomplete, Button, Chip, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import {
    getWasteReceiverResultTemplate,
    getWasteReceiverSystemPrompt,
    normalizeWasteReceiverDeploymentConfig,
    normalizeWasteReceiverUserEvaluationPrioritization,
} from '../../../common/wasteReceivers.js';
import {
    normalizeColumnReferencesToNames,
    normalizeTransitionColumns,
} from '../../../common/wasteReceiverSortingListTransition.js';
import PromptEditor, { PromptHighlight } from './PromptEditor.jsx';
import ResultTemplateInlinePreview from './ResultTemplateInlinePreview.jsx';
import { renderUserChips } from '../SetupChips.jsx';

const WasteReceiverPanel = ({
    tab,
    wasteReceivers,
    companyOptionIds,
    userOptionIds,
    companyById,
    userById,
    loadingReceivers,
    loadingCompanies,
    loadingUsers,
    companyLoadError,
    userLoadError,
    saving,
    getCompanyDisplayLabel,
    getUserDisplayLabel,
}) => {
    const {
        addDraftColumn,
        draft,
        draftColumnById,
        draftColumnOptionIds,
        draftGroupingColumnOptionIds,
        draftGroupingColumns,
        draftMode,
        draftReadAloudColumns,
        draftResultTemplate,
        draftSystemPrompt,
        formTitle,
        isCreating,
        moveDraftPrioritizedUser,
        receiverUserCountById,
        removeDraftColumn,
        selectedReceiver,
        selectedReceiverId,
        selectedReceiverUserIds,
        setDraft,
        setResultTemplateDialogOpen,
        setSelectedReceiverId,
        setSystemPromptDialogOpen,
        showColumnIds,
        startCreate,
        startEdit,
        updateDraftColumn,
        updateDraftCompanyIds,
        updateDraftDisclaimer,
        updateDraftGroupingColumns,
        updateDraftNoShowColumns,
        updateDraftReadAloudColumns,
        updateDraftSystemPrompt,
        updateDraftUserEvaluationPrioritization,
        updateDraftUserIds,
    } = tab;

    const renderCompanyChips = (companyIds = []) => {
        if (companyIds.length === 0) {
            return <span className="empty-inline">Ingen virksomheder</span>;
        }

        return (
            <div className="company-chip-list">
                {companyIds.map((companyId) => (
                    <Chip
                        key={companyId}
                        size="small"
                        label={getCompanyDisplayLabel(companyId)}
                        className="setup-chip"
                    />
                ))}
            </div>
        );
    };

    const renderColumnChips = (columns = []) => {
        const normalizedColumns = normalizeTransitionColumns(columns);
        if (normalizedColumns.length === 0) {
            return <span className="empty-inline">Ingen kolonner</span>;
        }

        return (
            <div className="column-chip-list">
                {normalizedColumns.map((column) => (
                    <Chip
                        key={column.id || column.display_name}
                        size="small"
                        label={column.id
                            ? `${column.display_name} (id: ${column.id})`
                            : column.display_name}
                        className="setup-chip"
                    />
                ))}
            </div>
        );
    };

    const renderReferenceChips = (references = [], columns = [], emptyLabel) => {
        const names = normalizeColumnReferencesToNames(references, columns);
        if (names.length === 0) {
            return <span className="empty-inline">{emptyLabel}</span>;
        }

        return (
            <div className="column-chip-list">
                {names.map((name) => (
                    <Chip key={name} size="small" label={name} className="setup-chip" />
                ))}
            </div>
        );
    };

    const renderHiddenColumnChips = (references = [], columns = []) => {
        if (normalizeColumnReferencesToNames(references, columns).length === 0) {
            return <span className="empty-inline">Ingen skjulte kolonner</span>;
        }
        return renderReferenceChips(references, columns, 'Ingen skjulte kolonner');
    };

    const renderGroupingColumnChips = (references = [], columns = []) => {
        return renderReferenceChips(references, columns, 'Ingen grupperingskolonner');
    };

    const renderReadAloudColumnChips = (references = [], columns = []) => {
        return renderReferenceChips(references, columns, 'Ingen oplæste kolonner');
    };

    const renderSystemPromptText = (deploymentConfig) => {
        const systemPrompt = getWasteReceiverSystemPrompt(deploymentConfig);
        if (!systemPrompt.trim()) {
            return <span className="empty-inline">Ikke angivet</span>;
        }

        return <PromptHighlight value={systemPrompt} className="setup-prompt-preview" />;
    };

    const renderResultTemplateSummary = (deploymentConfig) => {
        const resultTemplate = getWasteReceiverResultTemplate(deploymentConfig);
        if (!resultTemplate.isCustom) {
            return <span className="empty-inline">Stationens standardskærm</span>;
        }

        return (
            <span className="setup-result-template-summary">
                <Chip size="small" label="Tilpasset" className="setup-chip" />
                {resultTemplate.updatedAt ? (
                    <span className="setup-users-secondary">
                        Ændret {new Date(resultTemplate.updatedAt).toLocaleDateString('da-DK')}
                    </span>
                ) : null}
            </span>
        );
    };

    const renderDisclaimerText = (deploymentConfig) => {
        const normalizedDeploymentConfig = normalizeWasteReceiverDeploymentConfig(deploymentConfig);
        if (!normalizedDeploymentConfig.disclaimer.trim()) {
            return <span className="empty-inline">Skjult i appen</span>;
        }

        return (
            <span className="setup-disclaimer-text">
                {normalizedDeploymentConfig.disclaimer}
            </span>
        );
    };

    const renderForm = () => (
        <div className="setup-detail-panel">
            <div className="setup-detail-header">
                <div>
                    <h2>{formTitle}</h2>
                    <p>
                        {isCreating
                            ? 'Firestore-id genereres automatisk ved oprettelse.'
                            : selectedReceiver?.id}
                    </p>
                </div>
            </div>

            <div className="setup-form">
                <TextField
                    label="Navn"
                    value={draft.name}
                    onChange={(event) =>
                        setDraft((currentDraft) => ({
                            ...currentDraft,
                            name: event.target.value,
                        }))
                    }
                    required
                    size="small"
                    fullWidth
                    autoFocus
                />

                <Autocomplete
                    multiple
                    freeSolo
                    filterSelectedOptions
                    classes={{
                        paper: 'setup-autocomplete-paper',
                        listbox: 'setup-autocomplete-listbox',
                        option: 'setup-autocomplete-option',
                    }}
                    options={companyOptionIds}
                    value={draft.companies}
                    onChange={(_, value) => updateDraftCompanyIds(value)}
                    getOptionLabel={(option) => getCompanyDisplayLabel(option)}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;

                        return (
                            <li key={key} {...optionProps}>
                                <div className="company-option">
                                    <span>{companyById.get(option)?.label || option}</span>
                                    <span>{option}</span>
                                </div>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });

                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={getCompanyDisplayLabel(option)}
                                    className="setup-chip"
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Virksomheder"
                            helperText=""
                            size="small"
                        />
                    )}
                />

                {companyLoadError && <Alert severity="warning">{companyLoadError}</Alert>}

                <Autocomplete
                    multiple
                    filterSelectedOptions
                    loading={loadingUsers}
                    disabled={saving || loadingUsers || Boolean(userLoadError)}
                    classes={{
                        paper: 'setup-autocomplete-paper',
                        listbox: 'setup-autocomplete-listbox',
                        option: 'setup-autocomplete-option',
                    }}
                    options={userOptionIds}
                    value={draft.userIds}
                    onChange={(_, value) => updateDraftUserIds(value)}
                    getOptionLabel={(option) => getUserDisplayLabel(option)}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const user = userById.get(option);

                        return (
                            <li key={key} {...optionProps}>
                                <div className="user-option">
                                    <span>{user?.label || option}</span>
                                    <span>{user?.secondaryLabel || option}</span>
                                </div>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });

                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={getUserDisplayLabel(option)}
                                    className="setup-chip"
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Brugere"
                            size="small"
                        />
                    )}
                />

                <Autocomplete
                    multiple
                    filterSelectedOptions
                    disabled={saving || draft.userIds.length === 0}
                    classes={{
                        paper: 'setup-autocomplete-paper',
                        listbox: 'setup-autocomplete-listbox',
                        option: 'setup-autocomplete-option',
                    }}
                    options={draft.userIds}
                    value={normalizeWasteReceiverUserEvaluationPrioritization(
                        draft.userEvaluationPrioritization,
                        draft.userIds,
                    )}
                    onChange={(_, value) => updateDraftUserEvaluationPrioritization(value)}
                    getOptionLabel={(option) => getUserDisplayLabel(option)}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const user = userById.get(option);

                        return (
                            <li key={key} {...optionProps}>
                                <div className="user-option">
                                    <span>{user?.label || option}</span>
                                    <span>{user?.secondaryLabel || option}</span>
                                </div>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });

                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={getUserDisplayLabel(option)}
                                    className="setup-chip"
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="User evaluation prioritisation"
                            helperText="Vælg prioriterede brugere fra listen ovenfor. Øverste bruger har højest prioritet."
                            size="small"
                        />
                    )}
                />

                {normalizeWasteReceiverUserEvaluationPrioritization(
                    draft.userEvaluationPrioritization,
                    draft.userIds,
                ).length > 0 && (
                    <div className="column-editor-list">
                        {normalizeWasteReceiverUserEvaluationPrioritization(
                            draft.userEvaluationPrioritization,
                            draft.userIds,
                        ).map((userId, index, prioritizedUsers) => (
                            <div className="column-editor-row" key={`prioritized-user-${userId}`}>
                                <TextField
                                    value={`${index + 1}. ${getUserDisplayLabel(userId)}`}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => moveDraftPrioritizedUser(index, -1)}
                                        disabled={saving || index === 0}
                                    >
                                        Op
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => moveDraftPrioritizedUser(index, 1)}
                                        disabled={saving || index === prioritizedUsers.length - 1}
                                    >
                                        Ned
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {userLoadError && <Alert severity="warning">{userLoadError}</Alert>}

                <TextField
                    label="Disclaimer"
                    value={draft.deploymentConfig?.disclaimer || ''}
                    onChange={(event) => updateDraftDisclaimer(event.target.value)}
                    helperText="Vises i mobilappen for denne affaldsmodtager. Tomt felt skjuler disclaimerboksen."
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                />

                <div className="setup-form-section">
                    <div className="setup-form-section-header">
                        <h3>Kolonner</h3>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addDraftColumn}
                            disabled={saving}
                            size="small"
                        >
                            Tilføj kolonne
                        </Button>
                    </div>

                    <div className={`column-editor-list${showColumnIds ? '' : ' name-only'}`}>
                        <div className="column-editor-header">
                            {showColumnIds && <span>Kolonne-id</span>}
                            <span>Kolonnenavn</span>
                            <span />
                        </div>
                        {draft.columns.map((column, index) => (
                            <div className="column-editor-row" key={`column-${index}`}>
                                {showColumnIds && (
                                    <TextField
                                        value={column.id || ''}
                                        onChange={(event) =>
                                            updateDraftColumn(index, 'id', event.target.value)
                                        }
                                        size="small"
                                        fullWidth
                                        inputProps={{ 'aria-label': 'Kolonne-id' }}
                                    />
                                )}
                                <TextField
                                    value={column.display_name}
                                    onChange={(event) =>
                                        updateDraftColumn(index, 'display_name', event.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    required
                                    inputProps={{ 'aria-label': 'Kolonnenavn' }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => removeDraftColumn(index)}
                                    disabled={saving}
                                    className="column-remove-button"
                                >
                                    Fjern
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>

                <Autocomplete
                    multiple
                    filterSelectedOptions
                    disabled={saving || draftColumnOptionIds.length === 0}
                    classes={{
                        paper: 'setup-autocomplete-paper',
                        listbox: 'setup-autocomplete-listbox',
                        option: 'setup-autocomplete-option',
                    }}
                    options={draftColumnOptionIds}
                    value={normalizeColumnReferencesToNames(draft.noShowColumns, draft.columns)
                        .filter((columnName) => draftColumnById.has(columnName))}
                    onChange={(_, value) => updateDraftNoShowColumns(value)}
                    getOptionLabel={(option) => {
                        const column = draftColumnById.get(option);
                        return column?.display_name || option;
                    }}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const column = draftColumnById.get(option);

                        return (
                            <li key={key} {...optionProps}>
                                <div className="column-option">
                                    <span>{column?.display_name || option}</span>
                                    {column?.id && <span>Id: {column.id}</span>}
                                </div>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            const column = draftColumnById.get(option);

                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={column?.display_name || option}
                                    className="setup-chip"
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Skjulte kolonner"
                            size="small"
                        />
                    )}
                />

                <Autocomplete
                    multiple
                    filterSelectedOptions
                    disabled={saving || draftGroupingColumnOptionIds.length === 0}
                    classes={{
                        paper: 'setup-autocomplete-paper',
                        listbox: 'setup-autocomplete-listbox',
                        option: 'setup-autocomplete-option',
                    }}
                    options={draftGroupingColumnOptionIds}
                    value={draftGroupingColumns}
                    onChange={(_, value) => updateDraftGroupingColumns(value)}
                    getOptionLabel={(option) => {
                        const column = draftColumnById.get(option);
                        return column?.display_name || option;
                    }}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const column = draftColumnById.get(option);

                        return (
                            <li key={key} {...optionProps}>
                                <div className="column-option">
                                    <span>{column?.display_name || option}</span>
                                    {column?.id && <span>Id: {column.id}</span>}
                                </div>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            const column = draftColumnById.get(option);

                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={column?.display_name || option}
                                    className="setup-chip"
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Grupperingskolonner"
                            size="small"
                            helperText="Værdierne i de valgte kolonner udgør tilsammen den nøgle, affaldsregistreringer grupperes efter."
                        />
                    )}
                />

                <Autocomplete
                    multiple
                    filterSelectedOptions
                    disabled={saving || draftColumnOptionIds.length === 0}
                    classes={{
                        paper: 'setup-autocomplete-paper',
                        listbox: 'setup-autocomplete-listbox',
                        option: 'setup-autocomplete-option',
                    }}
                    options={draftColumnOptionIds}
                    value={draftReadAloudColumns}
                    onChange={(_, value) => updateDraftReadAloudColumns(value)}
                    getOptionLabel={(option) => {
                        const column = draftColumnById.get(option);
                        return column?.display_name || option;
                    }}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const column = draftColumnById.get(option);

                        return (
                            <li key={key} {...optionProps}>
                                <div className="column-option">
                                    <span>{column?.display_name || option}</span>
                                    {column?.id && <span>Id: {column.id}</span>}
                                </div>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            const column = draftColumnById.get(option);

                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    label={column?.display_name || option}
                                    className="setup-chip"
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Oplæste kolonner"
                            size="small"
                        />
                    )}
                />
            </div>

            <div className="setup-form-section">
                <div className="setup-form-section-header">
                    <h3>System prompt</h3>
                    <Button
                        variant="outlined"
                        startIcon={<OpenInFullIcon />}
                        onClick={() => setSystemPromptDialogOpen(true)}
                        size="small"
                    >
                        Forstør
                    </Button>
                </div>

                <PromptEditor
                    label="System prompt"
                    value={draftSystemPrompt}
                    onChange={updateDraftSystemPrompt}
                    disabled={saving}
                    // Fixed at the disclaimer box's height: a system prompt is far too long
                    // to grow the form, so the box scrolls and "Forstør" opens it in full.
                    minRows={3}
                    maxRows={3}
                    helperText="Sendes til modellen for denne affaldsmodtager. {{...}} er pladsholdere, der udfyldes automatisk."
                />
            </div>

            <div className="setup-form-section">
                <div className="setup-form-section-header">
                    <h3>Resultatskærm</h3>
                </div>

                <p className="setup-form-section-note">
                    {draftResultTemplate.isCustom
                        ? 'Tilpasset result.html og result.css for denne affaldsmodtager.'
                        : 'Stationen bruger sin indbyggede resultatskærm.'}
                </p>

                <ResultTemplateInlinePreview
                    // A receiver gets its Firestore id on first save, and the preview reads
                    // that document, so one being created has nothing to render against yet.
                    wasteReceiverId={isCreating ? '' : selectedReceiver?.id || ''}
                    html={draftResultTemplate.html}
                    css={draftResultTemplate.css}
                    // Sits above the frame, right-aligned to it: the button belongs to the
                    // screen it edits, not to the width of the form.
                    action={(
                        <Button
                            variant="outlined"
                            startIcon={<OpenInFullIcon />}
                            onClick={() => setResultTemplateDialogOpen(true)}
                            size="small"
                            disabled={saving}
                        >
                            Rediger
                        </Button>
                    )}
                />
            </div>

        </div>
    );

    const renderSelectedReceiver = () => {
        if (!selectedReceiver) {
            return (
                <div className="setup-detail-panel empty-state">
                    <h2>Ingen affaldsmodtager valgt</h2>
                    <p>Opret den første affaldsmodtager for at komme i gang.</p>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>
                        Ny affaldsmodtager
                    </Button>
                </div>
            );
        }

        return (
            <div className="setup-detail-panel">
                <div className="setup-detail-header">
                    <div>
                        <h2>{selectedReceiver.name || 'Uden navn'}</h2>
                        <p>{selectedReceiver.id}</p>
                    </div>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={startEdit}
                        disabled={saving || loadingUsers}
                    >
                        Rediger
                    </Button>
                </div>

                <div className="setup-field-grid">
                    <div className="setup-field-label">Navn</div>
                    <div>{selectedReceiver.name || <span className="empty-inline">Ikke angivet</span>}</div>

                    <div className="setup-field-label">Virksomheder</div>
                    <div>{renderCompanyChips(selectedReceiver.companies)}</div>

                    <div className="setup-field-label">Brugere</div>
                    <div>{renderUserChips(selectedReceiverUserIds, getUserDisplayLabel)}</div>

                    <div className="setup-field-label">User evaluation prioritisation</div>
                    <div>
                        {renderUserChips(
                            normalizeWasteReceiverUserEvaluationPrioritization(
                                selectedReceiver.userEvaluationPrioritization,
                                selectedReceiverUserIds,
                            ),
                            getUserDisplayLabel,
                        )}
                    </div>

                    <div className="setup-field-label">Disclaimer</div>
                    <div>{renderDisclaimerText(selectedReceiver.deploymentConfig)}</div>

                    <div className="setup-field-label">Kolonner</div>
                    <div>{renderColumnChips(selectedReceiver.columns)}</div>

                    <div className="setup-field-label">Skjulte kolonner</div>
                    <div>
                        {renderHiddenColumnChips(
                            selectedReceiver.noShowColumns,
                            selectedReceiver.columns,
                        )}
                    </div>

                    <div className="setup-field-label">Grupperingskolonner</div>
                    <div>
                        {renderGroupingColumnChips(
                            selectedReceiver.groupingColumns,
                            selectedReceiver.columns,
                            selectedReceiver.noShowColumns,
                        )}
                    </div>

                    <div className="setup-field-label">Oplæste kolonner</div>
                    <div>
                        {renderReadAloudColumnChips(
                            selectedReceiver.readAloudColumns,
                            selectedReceiver.columns,
                        )}
                    </div>

                    <div className="setup-field-label">System prompt</div>
                    <div>{renderSystemPromptText(selectedReceiver.deploymentConfig)}</div>

                    <div className="setup-field-label">Resultatskærm</div>
                    <div>{renderResultTemplateSummary(selectedReceiver.deploymentConfig)}</div>
                </div>
            </div>
        );
    };

    return (
        <div className="setup-layout">
            <div className="setup-list-panel">
                <div className="setup-list-header">
                    <h2>Affaldsmodtagere</h2>
                </div>

                <div className="receiver-list">
                    {wasteReceivers.length === 0 && !loadingReceivers ? (
                        <Alert severity="info">Der er endnu ingen affaldsmodtagere.</Alert>
                    ) : null}

                    {wasteReceivers.map((receiver) => (
                        <button
                            key={receiver.id}
                            type="button"
                            className={`receiver-row ${
                                receiver.id === selectedReceiverId ? 'active' : ''
                            }`}
                            onClick={() => setSelectedReceiverId(receiver.id)}
                            disabled={draftMode !== 'view'}
                        >
                            <span className="receiver-name">{receiver.name || 'Uden navn'}</span>
                            <span className="receiver-meta">
                                {receiver.id} · {receiver.companies.length} virksomheder ·{' '}
                                {receiverUserCountById.get(receiver.id) || 0} brugere ·{' '}
                                {receiver.columns.length} kolonner
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {draftMode === 'view' ? renderSelectedReceiver() : renderForm()}
        </div>
    );
};

export default WasteReceiverPanel;
